import React, { useState, useRef, useEffect } from "react";
import { Flex, PADDING } from "styles/global-style";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { MenuItem } from "components/Menus/style";
import ButtonTiny from "components/ButtonTiny";
import Column from "./Column";
import { Draggable } from "@hello-pangea/dnd";
import foldarrow from "imgs/foldarrow.png";

export default function Story({ indexPath, story, index, goalId, deleteStoryCb, completeStoryCb, reactivateStoryCb, addTaskCb, onClickStoryCb, editTaskCb, users, usersForDisplay, selectedStory, selectedTask, totalStories, collapsed, onToggleCollapse, columns, isEditing, onSaveTitle, editingTaskId, onSaveTaskTitle, onSaveTaskEstimate, onSaveTaskAssignee, storyRef }) {
  const [hovering, setHovering] = useState(false);
  const [titleValue, setTitleValue] = useState(story?.title || "");
  const inputRef = useRef(null);

  // Sync titleValue with story title
  useEffect(() => {
    setTitleValue(story?.title || "");
  }, [story?.title]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const getStyle = (isDragging, draggableStyle) => ({
    marginBottom: 10,
    position: "relative",
    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const isCompleted = story?.status === "COMPLETE";

  return (
    <Draggable 
      key={indexPath.join("-")} 
      draggableId={indexPath.join("-")} 
      index={index} 
      type="story"
      isDragDisabled={isEditing}
    >
      {(provided, snapshot) => (
        <div
          {...provided.draggableProps}
          ref={(node) => {
            provided.innerRef(node);
            if (storyRef) {
              storyRef(node);
            }
          }}
          style={getStyle(snapshot.isDragging, provided.draggableProps.style)}
          onMouseOver={() => setHovering(true)}
          onMouseOut={() => setHovering(false)}
        >
          {selectedStory === index && selectedTask === -1 && (
            <div
              style={{
                position: "absolute",
                top: 2,
                right: -15,
                bottom: collapsed ? -1 : -6,
                left: -14,
                border: "1px solid orange",
                borderRadius: 14,
                zIndex: -10,
              }}
            />
          )}
          <div
            style={{
              position: "relative",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize:28,
              fontWeight: "bold",
              color: isCompleted ? "#82c507" : "#fff",
              textShadow: "rgb(0, 0, 0) 1px 1px 3px",
              lineHeight: 1.6,
              marginRight: 10,
              opacity: 1,
              zIndex: 10,
              minWidth: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "1 1 0", minWidth: 0 }}>
              {/* Always show foldarrow in Board view */}
              <img
                src={foldarrow}
                alt="collapse"
                style={{
                  width: 11,
                  height: 11,
                  cursor: "pointer",
                  transform: collapsed ? "rotate(0deg)" : "rotate(90deg)",
                  transition: "transform 0.2s ease",
                  filter: isCompleted ? "invert(0)" : "invert(1)",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleCollapse && onToggleCollapse(story.id);
                }}
              />
              {isEditing ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  onBlur={() => {
                    if (onSaveTitle) {
                      // If title is empty, pass null to trigger deletion for new stories
                      if (!titleValue || !titleValue.trim()) {
                        onSaveTitle(null);
                      } else {
                        onSaveTitle(titleValue);
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur();
                      if (onSaveTitle) {
                        if (!titleValue || !titleValue.trim()) {
                          onSaveTitle(null);
                        } else {
                          onSaveTitle(titleValue);
                        }
                      }
                    } else if (e.key === 'Escape') {
                      setTitleValue(story?.title || "");
                      if (onSaveTitle) {
                        onSaveTitle(null); // Cancel editing or delete if new
                      }
                    }
                  }}
                  style={{
                    fontSize: 28,
                    fontWeight: "bold",
                    color: isCompleted ? "#82c507" : "#fff",
                    background: "transparent",
                    border: "none",
                    borderRadius: 4,
                    padding: "4px 8px",
                    outline: "none",
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    flex: "1 1 0",
                    boxSizing: "border-box",
                    textShadow: "rgb(0, 0, 0) 1px 1px 3px",
                  }}
                  placeholder="Story title..."
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              ) : (
                <div
                  {...provided.dragHandleProps}
                  style={{
                    flex: "1 1 0",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  onClick={() => onClickStoryCb(goalId, story.id)}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {story?.title || "Untitled Story"}
                  </span>
                  {isCompleted && (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "2px 8px",
                        borderRadius: 4,
                        fontSize: "10px",
                        fontWeight: "700",
                        background: "#82c507",
                        color: "#000",
                        textShadow: "none",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        marginLeft: 8,
                      }}
                    >
                      COMPLETED
                    </div>
                  )}
                </div>
              )}
            </div>
            <Popover className="popover">
              <PopoverButton className="popover-button">
                <ButtonTiny dots />
              </PopoverButton>
              <PopoverPanel className="popover-panel" transition anchor={{ to: "bottom start", padding: "34px" }}>
                {({ close }) => (
                  <>
                    {isCompleted ? (
                      <MenuItem 
                        close={close}
                        click={() => {
                          if (reactivateStoryCb) reactivateStoryCb(indexPath, story.id);
                        }}
                      >
                        <div style={{ color: "#82c507", fontWeight: "bold", width: "100%" }}>
                          Re-activate Story
                        </div>
                      </MenuItem>
                    ) : (
                      <MenuItem 
                        close={close}
                        click={() => {
                          if (completeStoryCb) completeStoryCb(indexPath, story.id);
                        }}
                      >
                        <div style={{ color: "#82c507", fontWeight: "bold", width: "100%" }}>
                          Complete Story
                        </div>
                      </MenuItem>
                    )}
                    <MenuItem 
                      close={close}
                      click={() => deleteStoryCb(indexPath, story.id, story.title)}
                    >
                      <div style={{ color: "red", fontWeight: "bold", width: "100%" }}>
                        Delete story
                      </div>
                    </MenuItem>
                  </>
                )}
              </PopoverPanel>
            </Popover>
          </div>

          {!collapsed && (
            <div
              style={{
                borderRadius: 10,
              }}
            >
              <Flex gap={PADDING / 2} style={{ alignItems: "stretch" }}>
                {columns && columns.length > 0 && (() => {
                  return columns.map((col, j) => {
                    // Filter tasks for this column based on status
                    const columnTasks = story.children ? story.children.filter(task => task.status === col.id) : [];
                    
                    // Calculate task offset for selection
                    let taskOffset = 0;
                    for (let k = 0; k < j; k++) {
                      const prevColumnTasks = story.children ? story.children.filter(task => task.status === columns[k].id) : [];
                      taskOffset += prevColumnTasks.length;
                    }
                    
                    return (
                      <Column
                        key={"keycolumn" + j}
                        indexPath={[...indexPath, j]}
                        col={col}
                        storyIndex={index}
                        storyId={story.id}
                        index={j}
                        width={100 / columns.length}
                        isFirst={j === 0}
                        showAdd={hovering}
                        addTaskCb={addTaskCb}
                        editTaskCb={(task, isTitleClick) => editTaskCb(task, story, col, j, isTitleClick)}
                        users={users}
                        usersForDisplay={usersForDisplay}
                        isEndColumn={j === columns.length - 1}
                        selectedStory={selectedStory}
                        selectedTask={selectedTask - taskOffset}
                        totalStories={totalStories}
                        tasks={columnTasks}
                        editingTaskId={editingTaskId}
                        onSaveTaskTitle={onSaveTaskTitle}
                        onSaveTaskEstimate={onSaveTaskEstimate}
                        onSaveTaskAssignee={onSaveTaskAssignee}
                        isCompleted={isCompleted}
                      />
                    );
                  });
                })()}
              </Flex>
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
}
